/**
 * 词汇数据结构
 */
export interface Vocabulary {
  id: string
  word: string
  meaning: string
  phonetic?: string
  example?: string
  sourceText: string  // 来自哪句翻译
  scenario: string    // 场景标签
  createdAt: number
  masteryLevel: number  // 掌握程度 0-5
  nextReview: number    // 下次复习时间
  reviewCount: number   // 复习次数
  userAnswer?: string   // 用户最近一次复习的答案（仅用于显示，不持久化）
}

/**
 * 复习计划配置（艾宾浩斯间隔）
 */
export const REVIEW_INTERVALS = [
  1,    // 第1天
  2,    // 第2天
  4,    // 第4天
  7,    // 第7天
  15,   // 第15天
  30    // 第30天
] as const

export type ReviewDay = typeof REVIEW_INTERVALS[number]

/**
 * 生词本存储结构
 */
export interface VocabularyStore {
  vocabulary: Vocabulary[]
  settings: {
    dailyNewWords: number
    reviewThreshold: number
  }
}

/**
 * 生成唯一ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * 计算下次复习时间
 */
export function calculateNextReview(
  currentLevel: number
): number {
  // 当前掌握等级对应的复习间隔
  const intervalDays = REVIEW_INTERVALS[Math.min(currentLevel, REVIEW_INTERVALS.length - 1)]
  const nextReview = Date.now() + intervalDays * 24 * 60 * 60 * 1000
  return nextReview
}

/**
 * 更新词汇掌握等级
 * @param correct 用户是否答对
 * @param currentLevel 当前掌握等级
 * @returns 新的掌握等级
 */
export function updateMasteryLevel(
  correct: boolean,
  currentLevel: number
): number {
  if (correct) {
    // 答对：升级，最高5级
    return Math.min(currentLevel + 1, 5)
  } else {
    // 答错：降级，最低0级
    return Math.max(currentLevel - 1, 0)
  }
}

/**
 * 过滤需要复习的词汇
 */
export function getWordsForReview(vocabulary: Vocabulary[]): Vocabulary[] {
  const now = Date.now()
  return vocabulary.filter(v => v.nextReview <= now)
}

/**
 * 过滤今天需要学习的新词
 */
export function getTodayNewWords(
  vocabulary: Vocabulary[],
  dailyLimit: number = 6
): Vocabulary[] {
  const today = new Date().toDateString()
  const todayWords = vocabulary.filter(v => {
    const createdDate = new Date(v.createdAt).toDateString()
    return createdDate === today
  })
  
  return todayWords.slice(0, dailyLimit)
}

/**
 * 生成本地存储key
 */
export const VOCABULARY_STORAGE_KEY = 'vocabulary-store-v1'

/**
 * 保存到localStorage
 */
export function saveVocabularyStore(store: VocabularyStore): void {
  try {
    localStorage.setItem(VOCABULARY_STORAGE_KEY, JSON.stringify(store))
  } catch (e) {
    console.error('Failed to save vocabulary store:', e)
  }
}

/**
 * 从localStorage加载
 */
export function loadVocabularyStore(): VocabularyStore | null {
  try {
    const data = localStorage.getItem(VOCABULARY_STORAGE_KEY)
    if (data) {
      return JSON.parse(data)
    }
  } catch (e) {
    console.error('Failed to load vocabulary store:', e)
  }
  return null
}

/**
 * 清理过期词汇（超过30天未复习的标记为需要复习）
 */
export function cleanupExpiredWords(vocabulary: Vocabulary[]): Vocabulary[] {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
  return vocabulary.map(v => {
    if (v.createdAt < thirtyDaysAgo && v.nextReview < Date.now()) {
      // 30天前创建且未复习，强制设为待复习
      return {
        ...v,
        nextReview: Date.now(),
        masteryLevel: 0
      }
    }
    return v
  })
}
